import { RefObject, useMemo } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router';
import classNames from 'classnames';
import { GameData, useApiContext, useGetFreeSpins, useWindowSize } from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { GameHeaderAccountIcon } from 'src/components/svg/Accounts';
import { Button } from 'src/components/Buttons/Button';
import { BigCloseGame } from 'src/components/svg/Closes';
import { ArrowSingleGame } from 'src/components/svg/Arrows';
import { useModalContext } from 'src/hooks/useModalContext';
import { useDrawer } from 'src/hooks/useDrawer';
import { useRoute } from 'src/router/hooks';
import { FullScreenButton } from './components/FullScreenButton';
import { HeaderBurgerIcon } from 'src/components/svg/HeaderBurgerIcon';
import { GameHeaderContent } from './components/GameHeaderContent';
import { useAuthFlow } from 'src/modules/authFlow/hooks/useAuthFlow';
import { LogoMobile } from 'src/config/components/LogoMobile';
import { Wallet } from 'src/components/svg/Wallet';
import { GameHeaderAuthBalance } from './components/GameHeaderAuthBalance';
import { useCheckForLuckyCard } from 'src/EXPERIMENTAL_LIB/modules/gpt/games';

export const Header = ({
  headerVisible,
  toggleHeader,
  isFullscreen,
  onFullscreenOpen,
  onFullscreenClose,
  headerRef,
  game
}: {
  headerVisible: boolean;
  toggleHeader: () => void;
  jackpotType?: string;
  game: GameData;
  isFullscreen: boolean;
  onFullscreenOpen: () => void;
  onFullscreenClose: () => void;
  headerRef: RefObject<HTMLDivElement>;
}) => {
  const { isDekstopLayout } = useWindowSize();
  const { isAuthenticated } = useApiContext();
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const { openModal } = useModalContext();
  const navigate = useNavigate();
  const { ROUTE } = useRoute();
  const { state } = useLocation();
  const { openLeftDrawer, closeDrawer } = useDrawer();
  const refetchInterval = useMemo(() => (headerVisible ? 5000 : 0), [headerVisible]);
  const { freeSpins } = useGetFreeSpins({ refetchInterval });
  const { startRegistration } = useAuthFlow();
  const { gameId, demo: isDemo } = useParams();
  const checkForLuckyCard = useCheckForLuckyCard();

  const handleClose = () => {
    if (!isDemo && gameId) {
      checkForLuckyCard({ gameId: Number(gameId) });
    }

    const prevPath =
      state && state.history
        ? generatePath(state.history + state.search, {
            lang: language.replace('en', '')
          })
        : ROUTE.MAIN.get();

    navigate(prevPath);
  };

  const handleProfileClick = () => {
    if (isAuthenticated) {
      navigate(ROUTE.ACCOUNT_PROFILE_MAIN.get());
    } else startRegistration();
  };

  const handleDepositClick = () => {
    openModal('DEPOSIT');
    closeDrawer();
  };

  return (
    <div
      ref={headerRef}
      className={classNames(
        'flex flex-col w-[100%] fixed transition-[transform] top-0 z-50',
        headerVisible ? 'translate-y-0' : 'translate-y-[-100%]'
      )}>
      <div className="flex items-center w-full gap-2.5 px-2.5 header-game max-w-[2560px] lg:px-[100px]">
        <div
          onClick={() => {
            openLeftDrawer();
          }}
          className="my-4">
          <HeaderBurgerIcon className="[&_path]:fill-n_burger_icon" isGame={true} />
        </div>
        <LogoMobile
          className="max-w-[35px] md:max-w-[45px] hidden sm:block cursor-pointer my-4"
          role="link"
          onClick={() => navigate(ROUTE.MAIN.get())}
        />
        <div className="flex flex-row items-center gap-2.5 justify-end flex-1 my-4">
          {isAuthenticated ? (
            <>
              <button
                className="rounded-full p-2 bg-WLLET_GAME_HEADER_BG"
                onClick={handleDepositClick}>
                <Wallet />
              </button>
              {isAuthenticated && <GameHeaderAuthBalance />}
              {isDekstopLayout && (
                <div onClick={handleProfileClick} className="cursor-pointer">
                  <GameHeaderAccountIcon />
                </div>
              )}
            </>
          ) : (
            <Button
              title={`${t('general.deposit')}`}
              mode="button-secondary"
              onClick={() => startRegistration()}
              className="shadow-xl h-[35px] w-auto px-4 max-w-[140px] min-w-[140px]"
              label={`${t('general.deposit')}`}
            />
          )}
          {isDekstopLayout && (
            <FullScreenButton
              isFullScreen={isFullscreen}
              onFullscreenOpen={onFullscreenOpen}
              onFullscreenClose={onFullscreenClose}
            />
          )}
          <button onClick={handleClose}>
            <BigCloseGame className="max-w-[35px] md:max-w-[45px]" />
          </button>
        </div>
      </div>

      <GameHeaderContent game={game} freeSpins={freeSpins} />

      <div
        className={classNames(
          'absolute left-0 top-[100%] cursor-pointer bg-sgp_header_arrow_bg px-2.5 768:py-3 py-[6px] rounded-b-[4px]',
          !isFullscreen && 'lg:hidden'
        )}
        onClick={toggleHeader}>
        <ArrowSingleGame
          className="w-[17px] h-[17px] 768:w-[34px] 768:h-[34px]"
          style={{
            transform: !headerVisible ? 'rotateZ(180deg)' : 'rotateZ(0deg)'
          }}
        />
      </div>
    </div>
  );
};
